/**
 * ============================================================================
 * MonthlyChallengeCard.tsx - Monthly Challenges Leaderboard Component
 * ============================================================================
 * 
 * Displays:
 * - ACTIVE: Current challenge with leaderboard, time remaining, prizes
 * - COMPLETED: Last 3 winners + next challenge preview
 * 
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Badge,
  Avatar,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import { Trophy, Clock, Calendar } from 'lucide-react';
import { keyframes } from '@emotion/react';
import challengeService, { 
  ChallengeLeaderboard, 
  CHALLENGE_CONFIG,
  ChallengeType 
} from '../services/challengeService';

// ============================================================================
// ANIMATIONS
// ============================================================================

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.3); }
  50% { box-shadow: 0 0 25px rgba(255, 215, 0, 0.6); }
`;

// ============================================================================
// TYPES
// ============================================================================

interface Winner {
  odid: string;
  odidname?: string;
  odidusername?: string;
  odidavatar?: string;
  username?: string;
  avatar?: string;
  rank: number;
  score: number;
  prize: number;
}

interface MonthlyChallengeCardProps {
  currentUid?: string;
  maxEntries?: number;
  compact?: boolean;
}

// ============================================================================
// HELPER: Get next challenge type in rotation
// ============================================================================

const getNextChallengeType = (currentType: ChallengeType): ChallengeType => {
  const rotation: ChallengeType[] = ['hands_won', 'biggest_pot', 'win_streak', 'hands_played'];
  const currentIndex = rotation.indexOf(currentType);
  return rotation[(currentIndex + 1) % rotation.length];
};

// ============================================================================
// COMPONENT
// ============================================================================

const MonthlyChallengeCard: React.FC<MonthlyChallengeCardProps> = ({
  currentUid,
  maxEntries = 10,
  compact = false,
}) => {
  const [leaderboard, setLeaderboard] = useState<ChallengeLeaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [prizePool, setPrizePool] = useState<number>(15);

  // Fetch challenge data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await challengeService.getChallengeLeaderboard(undefined, maxEntries);
        setLeaderboard(data);
        
        // Get current prize pool
        const poolData = await challengeService.getCurrentPrizePool();
        setPrizePool(poolData.total);
      } catch (error) {
        console.error('Error fetching challenge:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [maxEntries]);

  // Subscribe to live updates (only for active challenges)
  useEffect(() => {
    if (!leaderboard?.challenge?.id || leaderboard.challenge.status === 'completed') return;

    const unsubscribe = challengeService.subscribeToLeaderboard(
      leaderboard.challenge.id,
      (entries) => {
        setLeaderboard(prev => prev ? { ...prev, entries } : null);
      },
      maxEntries
    );

    return () => unsubscribe();
  }, [leaderboard?.challenge?.id, leaderboard?.challenge?.status, maxEntries]);

  // Get challenge config
  const getConfig = (type: ChallengeType) => CHALLENGE_CONFIG[type];

  // Rank badge colors
  const getRankStyle = (rank: number) => {
    switch (rank) {
      case 1:
        return { bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: 'black', icon: '' };

      case 2:
        return { bg: 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)', color: 'black', icon: '' };

      case 3:
        return { bg: 'linear-gradient(135deg, #CD7F32 0%, #B87333 100%)', color: 'white', icon: '' };

      default:
        return { bg: 'rgba(255,255,255,0.1)', color: 'gray.300', icon: null };
    }
  };

  // Format score based on challenge type
  const formatScore = (score: number, type: ChallengeType): string => {
    return challengeService.formatScore(score, type);
  };

  // Get prize for rank
  const getPrize = (rank: number): string => {
    const breakdown = challengeService.getPrizeBreakdown(prizePool);
    switch (rank) {
      case 1: return `${breakdown.first.toFixed(0)} PSS`;
      case 2: return `${breakdown.second.toFixed(0)} PSS`;
      case 3: return `${breakdown.third.toFixed(0)} PSS`;
      default: return '';
    }
  };

  if (loading) {
    return (
      <Box
        bg="linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(13,27,42,0.95) 100%)"
        borderRadius="xl"
        border="2px solid"
        borderColor="purple.600"
        p={4}
        minH="200px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner color="purple.400" size="lg" />
      </Box>
    );
  }

  // No challenge exists
  if (!leaderboard || !leaderboard.challenge) {
    return (
      <Box
        bg="linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(13,27,42,0.95) 100%)"
        borderRadius="xl"
        border="2px solid"
        borderColor="purple.600"
        p={4}
      >
        <VStack spacing={3}>
          <Trophy size={32} color="#a78bfa" />
          <Text color="purple.400" fontWeight="bold">Monthly Challenge</Text>
          <Text color="gray.500" fontSize="sm" textAlign="center">
            No active challenge. Check back soon!
          </Text>
        </VStack>
      </Box>
    );
  }

  const { challenge, entries, userRank, userEntry, totalParticipants } = leaderboard;
  const config = getConfig(challenge.type);

  // ============================================================================
  // COMPLETED CHALLENGE - Show winners + next challenge
  // ============================================================================
  if (challenge.status === 'completed') {
    // Get winners from challenge data or construct from entries
    const winners: Winner[] = challenge.winners || entries.slice(0, 3).map((e, i) => ({
      odid: e.odid,
      odidname: e.odidname,
      odidusername: e.odidusername,
      odidavatar: e.odidavatar,
      rank: i + 1,
      score: e.score,
      prize: challenge.prizePool ? challenge.prizePool * [0.5, 0.3, 0.2][i] : 0,
    }));

    const nextType = getNextChallengeType(challenge.type);
    const nextConfig = getConfig(nextType);

    return (
      <Box
        bg="linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(13,27,42,0.95) 100%)"
        borderRadius="xl"
        border="2px solid"
        borderColor="purple.600"
        overflow="hidden"
      >
        {/* Header - Completed */}
        <Box
          bg="linear-gradient(135deg, rgba(34,197,94,0.3) 0%, rgba(22,163,74,0.3) 100%)"
          p={3}
          borderBottom="1px solid"
          borderColor="green.700"
        >
          <HStack justify="space-between">
            <HStack>
              <Trophy size={20} color="#22c55e" />
              <Text color="green.400" fontWeight="bold" fontSize="md">
                Challenge Complete!
              </Text>
            </HStack>
            <Badge colorScheme="green" variant="solid">
              ${(challenge.prizePool || prizePool).toFixed(0)} PSS Coins Awarded
            </Badge>
          </HStack>
        </Box>

        {/* Last Winners */}
        <Box p={3}>
          <Text color="gray.400" fontSize="xs" mb={2} textTransform="uppercase" letterSpacing="wide">
             Winners - {config.name}

          </Text>
          <VStack spacing={2} align="stretch">
            {winners.length > 0 ? winners.map((winner) => {
              const style = getRankStyle(winner.rank);
              const name = winner.odidname || winner.odidusername || winner.username || 'Player';
              const avatar = winner.odidavatar || winner.avatar;
              return (
                <HStack
                  key={winner.odid || winner.rank}
                  p={2}
                  bg="rgba(0,0,0,0.3)"
                  borderRadius="md"
                  spacing={3}
                >
                  <Box
                    w="28px"
                    h="28px"
                    borderRadius="full"
                    bg={style.bg}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Text fontSize="sm">{style.icon}</Text>
                  </Box>
                  <Avatar
                    size="xs"
                    src={avatar}
                    name={name}
                    bg="purple.600"
                  />
                  <Text color="white" fontSize="sm" fontWeight="medium" flex={1} isTruncated>
                    {name}
                  </Text>
                  <VStack align="end" spacing={0}>
                    <Text color="green.400" fontSize="sm" fontWeight="bold">
                      {formatScore(winner.score, challenge.type)}
                    </Text>
                    <Text color="yellow.400" fontSize="xs">
                      {winner.prize?.toFixed(0) || '0'} PSS
                    </Text>
                  </VStack>
                </HStack>
              );
            }) : (
              <Text color="gray.500" fontSize="sm" textAlign="center">No winners recorded</Text>
            )}
          </VStack>
        </Box>

        <Divider borderColor="purple.800" />

        {/* Next Challenge Preview */}
        <Box p={3} bg="rgba(0,0,0,0.2)">
          <HStack mb={2}>
            <Calendar size={14} color="#a78bfa" />
            <Text color="purple.400" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
              Next Challenge
            </Text>
          </HStack>
          <HStack spacing={3}>
            <Text fontSize="2xl">{nextConfig.icon}</Text>
            <VStack align="start" spacing={0}>
              <Text color="white" fontWeight="bold" fontSize="md">
                {nextConfig.name}
              </Text>
              <Text color="gray.400" fontSize="xs">
                {nextConfig.description}
              </Text>
            </VStack>
          </HStack>
          <HStack mt={2} spacing={2}>
            {nextConfig.pkcashMultiplier > 1 && (
              <Badge colorScheme="yellow" variant="outline" fontSize="2xs">
                PSS Coins {nextConfig.pkcashMultiplier}x
              </Badge>
            )}
            {nextConfig.playcoinsMultiplier > 0 && (
              <Badge colorScheme="cyan" variant="outline" fontSize="2xs">
                PlayCoins {nextConfig.playcoinsMultiplier}x
              </Badge>
            )}
            <Text color="gray.500" fontSize="xs">• Starts 1st of month</Text>
          </HStack>
        </Box>
      </Box>
    );
  }

  // ============================================================================
  // ACTIVE CHALLENGE - Show leaderboard
  // ============================================================================
  const timeRemaining = challengeService.getTimeRemaining(challenge);

  return (
    <Box
      bg="linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(13,27,42,0.95) 100%)"
      borderRadius="xl"
      border="2px solid"
      borderColor="purple.600"
      overflow="hidden"
      animation={`${glow} 3s ease-in-out infinite`}
    >
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(139,92,246,0.3) 0%, rgba(79,70,229,0.3) 100%)"
        p={3}
        borderBottom="1px solid"
        borderColor="purple.700"
      >
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={0}>
            <HStack>
              <Text fontSize="xl">{config.icon}</Text>
              <Text color="white" fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>
                {config.name}
              </Text>
            </HStack>
            <Text color="gray.400" fontSize="xs">
              {config.description}
            </Text>
          </VStack>
          
          <VStack align="end" spacing={0}>
            <HStack>
              <Clock size={12} color="#a78bfa" />
              <Text color="purple.300" fontSize="xs">
                {timeRemaining}
              </Text>
            </HStack>
            <Badge colorScheme="yellow" variant="solid" fontSize="xs">
              ${prizePool.toFixed(0)} PSS Coins
            </Badge>
          </VStack>
        </HStack>
      </Box>

      {/* Prize Breakdown (non-compact) */}
      {!compact && (
        <HStack 
          justify="center" 
          spacing={4} 
          py={2} 
          bg="rgba(0,0,0,0.2)"
          borderBottom="1px solid"
          borderColor="purple.800"
        >
          {[1, 2, 3].map(rank => {
            const style = getRankStyle(rank);
            return (
              <Tooltip 
                key={rank} 
                label={`${rank === 1 ? '1st' : rank === 2 ? '2nd' : '3rd'} Place Reward`}
              >
                <HStack spacing={1}>
                  <Text fontSize="sm">{style.icon}</Text>
                  <Text color="yellow.400" fontSize="sm" fontWeight="bold">
                    {getPrize(rank)}
                  </Text>
                </HStack>
              </Tooltip>
            );
          })}
        </HStack>
      )}

      {/* Leaderboard */}
      <VStack spacing={0} align="stretch" maxH={compact ? '200px' : '350px'} overflowY="auto">
        {entries.length === 0 ? (
          <Box p={4} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              No participants yet. Be the first!
            </Text>
          </Box>
        ) : (
          entries.map((entry, index) => {
            const rank = index + 1;
            const style = getRankStyle(rank);
            const isCurrentUser = entry.odid === currentUid;

            return (
              <HStack
                key={entry.odid}
                p={2}
                px={3}
                bg={isCurrentUser ? 'rgba(139,92,246,0.2)' : 'transparent'}
                borderLeft={isCurrentUser ? '3px solid' : 'none'}
                borderColor="purple.400"
                _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                spacing={3}
              >
                {/* Rank */}
                <Box
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bg={style.bg}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  {style.icon ? (
                    <Text fontSize="sm">{style.icon}</Text>
                  ) : (
                    <Text fontSize="xs" fontWeight="bold" color={style.color}>
                      {rank}
                    </Text>
                  )}
                </Box>

                {/* Avatar & Name */}
                <HStack flex={1} spacing={2} minW={0}>
                  <Avatar
                    size="xs"
                    src={entry.odidavatar}
                    name={entry.odidname || entry.odidusername}
                    bg="purple.600"
                  />
                  <VStack align="start" spacing={0} minW={0}>
                    <Text
                      color={isCurrentUser ? 'purple.300' : 'white'}
                      fontSize="sm"
                      fontWeight={isCurrentUser ? 'bold' : 'medium'}
                      isTruncated
                      maxW="100px"
                    >
                      {entry.odidname || entry.odidusername || 'Player'}
                      {isCurrentUser && ' (You)'}
                    </Text>
                  </VStack>
                </HStack>

                {/* Score */}
                <VStack align="end" spacing={0}>
                  <Text 
                    color={rank <= 3 ? 'yellow.400' : 'green.400'} 
                    fontSize="sm" 
                    fontWeight="bold"
                  >
                    {formatScore(entry.score, challenge.type)}
                  </Text>
                  {rank <= 3 && (
                    <Text color="yellow.600" fontSize="2xs">
                      {getPrize(rank)}
                    </Text>
                  )}
                </VStack>
              </HStack>
            );
          })
        )}
      </VStack>

      {/* User's rank if not in top entries */}
      {userEntry && userRank && userRank > maxEntries && (
        <Box
          p={2}
          px={3}
          bg="rgba(139,92,246,0.2)"
          borderTop="1px solid"
          borderColor="purple.700"
        >
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Box
                w="28px"
                h="28px"
                borderRadius="full"
                bg="purple.600"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" fontWeight="bold" color="white">
                  {userRank}
                </Text>
              </Box>
              <Text color="purple.300" fontSize="sm" fontWeight="bold">
                Your Rank
              </Text>
            </HStack>
            <Text color="green.400" fontSize="sm" fontWeight="bold">
              {formatScore(userEntry.score, challenge.type)}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Footer */}
      <Box
        p={2}
        bg="rgba(0,0,0,0.3)"
        borderTop="1px solid"
        borderColor="purple.800"
      >
        <HStack justify="space-between">
          <Text color="gray.500" fontSize="xs">
            {totalParticipants} participants
          </Text>
          <HStack spacing={1}>
            {config.pkcashMultiplier > 1 && (
              <Badge colorScheme="yellow" variant="outline" fontSize="2xs">
                PSS Coins {config.pkcashMultiplier}x
              </Badge>
            )}
            {config.playcoinsMultiplier > 0 && (
              <Badge colorScheme="cyan" variant="outline" fontSize="2xs">
                PlayCoins {config.playcoinsMultiplier}x
              </Badge>
            )}
          </HStack>
        </HStack>
      </Box>
    </Box>
  );
};

export default MonthlyChallengeCard;